import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Button from '../../components/common/Button';
import { statesApi, extractOne } from '../../api/facades';

const StateDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const numId = id ? parseInt(id, 10) : NaN;

  const { data: res, isLoading, isError } = useQuery({
    queryKey: ['states', numId],
    queryFn: () => statesApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const state = extractOne(res);

  if (Number.isNaN(numId) || isError || (!isLoading && !state)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <p className="text-slate-600 dark:text-slate-400">State not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/dashboard/settings/states')}>
          Back to list
        </Button>
      </div>
    );
  }

  const cities = (state && (state.cities ?? (state as any).cities)) || [];
  const citiesList = Array.isArray(cities) ? cities : [];

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200">
          {isLoading ? 'Loading…' : state?.name ?? 'State'}
        </h1>
        <Button variant="secondary" onClick={() => navigate('/dashboard/settings/states')}>
          Back to list
        </Button>
      </div>
      {isLoading ? (
        <p className="text-slate-500">Loading…</p>
      ) : (
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Country</dt>
            <dd className="text-slate-700 dark:text-slate-200">
              {(state as any).country?.name ?? (state as any).country_name ?? '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Cities</dt>
            <dd className="text-slate-700 dark:text-slate-200">{citiesList.length}</dd>
          </div>
        </dl>
      )}
      {!isLoading && citiesList.length > 0 && (
        <div className="mt-6">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Cities</h2>
          <ul className="divide-y divide-gray-200 dark:divide-gray-600">
            {citiesList.slice(0, 50).map((c: any) => (
              <li key={c.id} className="py-2 flex items-center justify-between">
                <span className="text-slate-700 dark:text-slate-200">{c.name}</span>
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/settings/cities/${c.id}`)}
                  className="text-primary-600 hover:text-primary-800 text-sm"
                >
                  View
                </button>
              </li>
            ))}
          </ul>
          {citiesList.length > 50 && (
            <p className="text-sm text-slate-500 mt-2">Showing first 50 of {citiesList.length} cities.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default StateDetailPage;
